/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { Text, View } from "react-native";
import LHDataTable from "../../components/LHDataTable";
import LHCheckbox from "../../components/LHCheckBox";
import LhToggleSwitch from "../../components/LhToggleSwitch";
import LhActionPlanButtonGroup from "../../components/LhActionPlanButtonGroup";
import { getActionPlanState, shouldAllowToggleInteraction } from "../../utils/actionPlanUtils";

type ColumnType = "toggle" | "checkbox";

type Column = {
  columnKey: string;
  header: string;
  type?: ColumnType;
  renderCell?: (item: any) => React.ReactNode;
  formatter?: (value: any, item?: any) => React.ReactNode;
  width?: number;
  flexGrow?: number;
};

type CheckboxField = "yesChecked" | "noChecked" | "naChecked";

type Props = {
  data: any[];
  columns: Column[];
  tableHeader?: string;
  tableDescription?: string;
  maxRecordsToShow?: number;
  onRowClick?: (rowData: any, colIdx?: number) => void;
  onToggleChange?: (id: number, value: boolean) => void;
  onCheckboxChange?: (id: number, field: CheckboxField, value: boolean) => void;
  columnFlexValues?: { [columnKey: string]: number };
  activeItemsData?: any[];
  isActiveItemsTable?: boolean;
  showHeaderTopBorder?: boolean;
  expansionRenderer?: (item: any) => React.ReactNode; // forwarded to LHDataTable for inline expansion
};

const SiteEvalDataTable: React.FC<Props> = ({
  data = [],
  columns = [],
  onRowClick,
  onToggleChange,
  onCheckboxChange,
  columnFlexValues,
  maxRecordsToShow,
  activeItemsData = [],
  isActiveItemsTable = false,
  showHeaderTopBorder = false,
  expansionRenderer,
}) => {
  const mappedColumns = React.useMemo(() => {
    return (columns || []).map((col) => {
      // If a custom renderCell already exists, keep it
      if (col.renderCell) return { ...col };

      // Handle toggle column types (including ActionPlan special case)
      if (col.type === "toggle") {
        const renderCell = (item: any) => {
          // Action plan status gets button group component
          if (col.columnKey === "ActionPlanStatus") {
            const actionPlanState = getActionPlanState(item, activeItemsData);
            const canInteract = shouldAllowToggleInteraction(item, activeItemsData, isActiveItemsTable);
            
            // Map the state to button status
            const buttonStatus = actionPlanState === "completed" ? "completed" : "incomplete";
            
            // If this is the Active Items tab, show a single status button styled like Action Plan table
            if (isActiveItemsTable) {
              return (
                <View style={{ minWidth: 120, alignItems: 'flex-start' }}>
                  <View
                    style={{
                      backgroundColor: buttonStatus === "completed" ? "#008553" : "#316BB8",
                      borderRadius: 4,
                      paddingVertical: 4,
                      paddingHorizontal: 16,
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>
                      {buttonStatus === "completed" ? "Completed" : "In Progress"}
                    </Text>
                  </View>
                </View>
              );
            }

            // All other tabs: show the two-button group
            return (
              <LhActionPlanButtonGroup
                status={buttonStatus}
                onStatusChange={
                  canInteract
                    ? (newStatus) => {
                        // Convert button status back to boolean toggle value
                        const newValue = newStatus === "completed";
                        onToggleChange && onToggleChange(item.id || item.Id, newValue);
                      }
                    : undefined
                }
              />
            );
          }

          return (
            <LhToggleSwitch
              value={!!item[col.columnKey]}
              onValueChange={() => onToggleChange && onToggleChange(item.id || item.Id, !item[col.columnKey])}
            />
          );
        };
        return { ...col, renderCell };
      }

      // Handle checkbox columns
      if (col.type === "checkbox") {
        const renderCell = (item: any) => (
          <LHCheckbox
            value={!!item[col.columnKey]}
            onValueChange={() => onCheckboxChange && onCheckboxChange(item.id || item.Id, col.columnKey as CheckboxField, !item[col.columnKey])}
          />
        );
        return { ...col, renderCell };
      }

      // If a formatter exists, convert it into a renderCell that can return
      // either a React node or plain text wrapped in <Text>
      if (col.formatter) {
        const renderCell = (item: any) => {
          try {
            const formatted = col.formatter!(item[col.columnKey], item);
            if (React.isValidElement(formatted)) return formatted;
            return <Text>{String(formatted !== undefined && formatted !== null ? formatted : item[col.columnKey] ?? "-")}</Text>;
          } catch (e) {
            return <Text>{String(item[col.columnKey] ?? "-")}</Text>;
          }
        };
        return { ...col, renderCell };
      }

      // Default: return column as-is
      return { ...col };
    });
  }, [columns, activeItemsData, isActiveItemsTable, onToggleChange, onCheckboxChange]);

  // LHDataTable's onRowClick expects a single argument. The shared LHDataTable
  // passes an item that may include `columnKey` for cell clicks; adapt to the
  // SiteEvalDataTable's expected signature (rowData, colIdx?).
  const handleRowClick = React.useCallback(
    (rowItem: any) => {
      if (!onRowClick) return;
      const colIdx = typeof rowItem.columnKey === "string" ? mappedColumns.findIndex((c) => c.columnKey === rowItem.columnKey) : undefined;
      onRowClick(rowItem, typeof colIdx === "number" && colIdx >= 0 ? colIdx : undefined);
    },
    [onRowClick, mappedColumns]
  );

  return (
    <LHDataTable
      data={data}
      columns={mappedColumns as any}
      onRowClick={handleRowClick}
      maxRecordsToShow={maxRecordsToShow}
      columnFlexValues={columnFlexValues}
      showHeaderTopBorder={showHeaderTopBorder}
      tableHeader={""}
      tableDescription={""}
      expansionRenderer={expansionRenderer}
    />
  );
};

export default SiteEvalDataTable;

